import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { getUserTrips, TripInfo, getUserStatus, UserStatus, createPortalSession } from '../services/api';
import { logger } from '../utils/logger';
import UnlockModal from '../components/UnlockModal';
import PrivacySection from '../components/dashboard/privacy/PrivacySection';
import CalendarSection from '../components/dashboard/CalendarSection';
import './DashboardPage.css';

type DashboardSection = 'trips' | 'calendar' | 'account' | 'settings' | 'privacy';

export default function DashboardPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [trips, setTrips] = useState<TripInfo[]>([]);
    const [status, setStatus] = useState<UserStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [showCelebration, setShowCelebration] = useState(false);
    const [activeSection, setActiveSection] = useState<DashboardSection>('trips');
    const [showUnlockModal, setShowUnlockModal] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        // Auto-switch to calendar if add-vacation action or section=calendar
        if (urlParams.get('action') === 'add-vacation' || urlParams.get('section') === 'calendar') {
            setActiveSection('calendar');
        }

        if (urlParams.get('subscribed') === 'true') {
            setShowCelebration(true);
            setTimeout(() => {
                window.history.replaceState({}, '', window.location.pathname);
            }, 5000);
        }

        // Auto-show upgrade modal if ?upgrade=true
        if (urlParams.get('upgrade') === 'true') {
            setShowUnlockModal(true);
            window.history.replaceState({}, '', window.location.pathname);
        }

        const fetchData = async () => {
            try {
                const [tripsRes, statusRes] = await Promise.all([
                    getUserTrips(),
                    getUserStatus()
                ]);
                setTrips(tripsRes.trips);
                setStatus(statusRes);
            } catch (err) {
                logger.error('Failed to load dashboard data:', err);
                navigate({ to: '/' });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate]);

    // Refresh user status (can be called by child components after changes)
    const refreshStatus = async () => {
        try {
            const statusRes = await getUserStatus();
            setStatus(statusRes);
        } catch (err) {
            logger.error('Failed to refresh status:', err);
        }
    };

    const handleClose = () => {
        if (window.history.length > 1) {
            window.history.back();
        } else {
            navigate({ to: '/' });
        }
    };

    if (loading) {
        return (
            <div className="dashboard-overlay">
                <div className="dashboard-modal loading">
                    <div className="loading-shimmer-dots">
                        <span></span><span></span><span></span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="dashboard-modal">
                {/* Mobile Toggle & Header (Sticky on phone) */}
                <div className="mobile-dashboard-header">
                    <div className="logo-small" onClick={() => navigate({ to: '/' })}>
                        <div className="logo-image-wrapper">
                            <img src="/raisa.png" alt="Raisa AI" className="logo-image" />
                        </div>
                        <span className="logo-text">Raisa<span>.</span></span>
                    </div>
                    <button className="close-modal-btn" onClick={handleClose}>
                        <CloseIcon />
                    </button>
                </div>

                {/* Sidebar */}
                <aside className="dashboard-sidebar">
                    <div className="sidebar-header desktop-only">
                        <div className="logo-small" onClick={() => navigate({ to: '/' })} style={{ cursor: 'pointer' }}>
                            <div className="logo-image-wrapper">
                                <img src="/raisa.png" alt="Raisa AI" className="logo-image" />
                            </div>
                            <span className="logo-text">Raisa<span>.</span></span>
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        <button
                            className={`nav-item ${activeSection === 'trips' ? 'active' : ''}`}
                            onClick={() => setActiveSection('trips')}
                        >
                            <div className="nav-icon-box"><TripsIcon /></div>
                            <span className="nav-label">{t('dashboard.my_itineraries')}</span>
                        </button>
                        <button
                            className={`nav-item ${activeSection === 'calendar' ? 'active' : ''}`}
                            onClick={() => setActiveSection('calendar')}
                        >
                            <div className="nav-icon-box"><CalendarIcon /></div>
                            <span className="nav-label">{t('calendar.title')}</span>
                        </button>
                        <button
                            className={`nav-item ${activeSection === 'account' ? 'active' : ''}`}
                            onClick={() => setActiveSection('account')}
                        >
                            <div className="nav-icon-box"><AccountIcon /></div>
                            <span className="nav-label">{t('dashboard.account_status')}</span>
                        </button>
                        <button
                            className={`nav-item ${activeSection === 'settings' ? 'active' : ''}`}
                            onClick={() => setActiveSection('settings')}
                        >
                            <div className="nav-icon-box"><SettingsIcon /></div>
                            <span className="nav-label">{t('common.settings')}</span>
                        </button>
                        <button
                            className={`nav-item ${activeSection === 'privacy' ? 'active' : ''}`}
                            onClick={() => setActiveSection('privacy')}
                        >
                            <div className="nav-icon-box"><PrivacyIcon /></div>
                            <span className="nav-label">{t('dashboard.privacy_data')}</span>
                        </button>
                    </nav>

                    <div className="sidebar-footer desktop-only">
                        <div className="user-brief">
                            <div className="user-avatar">{status?.isPremium ? <PremiumIcon /> : <AccountIcon />}</div>
                            <div className="user-info">
                                <span className="user-status-label">{status?.isPremium ? t('dashboard.premium') : t('dashboard.free_plan')}</span>
                            </div>
                        </div>
                        <button className="logout-btn" onClick={() => window.location.href = '/api/auth/logout'}>
                            <LogoutIcon /> {t('common.logout')}
                        </button>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="dashboard-main">
                    <header className="main-header">
                        <div className="header-title-group">
                            {activeSection === 'trips' && <h2>{t('dashboard.my_itineraries')}</h2>}
                            {activeSection === 'calendar' && <h2>{t('calendar.title')}</h2>}
                            {activeSection === 'account' && <h2>{t('dashboard.account_subscription')}</h2>}
                            {activeSection === 'settings' && <h2>{t('common.settings')}</h2>}
                            {activeSection === 'privacy' && <h2>{t('dashboard.privacy_data')}</h2>}
                        </div>
                        <div className="header-meta desktop-only">
                            <button className="close-modal-btn" onClick={handleClose}>
                                <CloseIcon />
                            </button>
                        </div>
                    </header>

                    <div className="main-content-area">
                        {showCelebration && (
                            <div className="celebration-banner-inner">
                                <div className="celebration-badge"><StarIcon /></div>
                                <div className="celebration-text">
                                    <h3>{t('dashboard.welcome_premium_title')}</h3>
                                    <p>{t('dashboard.welcome_premium_desc')}</p>
                                </div>
                                <button className="close-banner" onClick={() => setShowCelebration(false)}>✕</button>
                            </div>
                        )}

                        {activeSection === 'trips' && (
                            <div className="trips-section-view">
                                <div className="view-actions">
                                    <p className="trips-count">{t('dashboard.saved_trips_count', { count: trips.length })}</p>
                                    <button className="create-new-btn" onClick={() => navigate({ to: '/' })}>
                                        <PlusIcon /> {t('trip.plan_new')}
                                    </button>
                                </div>

                                <div className="trips-scroll-grid">
                                    {trips.length > 0 ? (
                                        trips.map((tripInfo, index) => (
                                            <TripCard
                                                key={tripInfo.trip.id}
                                                tripInfo={tripInfo}
                                                t={t}
                                                style={{ animationDelay: `${index * 0.05}s` }}
                                                onClick={() => navigate({ to: '/trip/$slug' as any, params: { slug: tripInfo.trip.slug } as any })}
                                            />
                                        ))
                                    ) : (
                                        <div className="empty-state-small">
                                            <p>{t('dashboard.no_trips')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeSection === 'calendar' && (
                            <CalendarSection />
                        )}

                        {activeSection === 'account' && (
                            <div className="account-section-view">
                                {/* Current Plan Card */}
                                <div className="premium-card-large">
                                    <div className="premium-card-header">
                                        <div className={`plan-icon ${status?.isPremium ? 'premium' : ''}`}>
                                            {status?.isPremium ? <PremiumIcon size={28} /> : <AccountIcon size={28} />}
                                        </div>
                                        <div className="plan-details">
                                            <h3>{status?.isPremium ? (status?.subscription?.cancelAtPeriodEnd ? t('dashboard.premium_cancelling') : t('dashboard.premium_subscription')) : t('dashboard.free_explorer_plan')}</h3>
                                            <p>
                                                {status?.isPremium
                                                    ? (status?.subscription?.cancelAtPeriodEnd
                                                        ? t('dashboard.cancelled_until', { date: new Date(status.subscription.currentPeriodEnd!).toLocaleDateString() })
                                                        : t('dashboard.next_billing'))
                                                    : t('dashboard.upgrade_unlimited')}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="entitlement-stats">
                                        <div className="e-stat">
                                            <span className="e-label">{t('dashboard.ai_edits')}</span>
                                            <span className="e-value">{status?.isPremium ? t('dashboard.unlimited') : t('dashboard.remaining_count', { count: status?.freeEditsRemaining ?? 0 })}</span>
                                        </div>
                                        <div className="e-stat">
                                            <span className="e-label">{t('dashboard.pdf_exports')}</span>
                                            <span className="e-value">{status?.isPremium ? t('dashboard.unlimited') : t('dashboard.none')}</span>
                                        </div>
                                    </div>

                                    {status?.isPremium ? (
                                        <button
                                            className="manage-billing-btn"
                                            onClick={async () => {
                                                try {
                                                    const { sessionUrl } = await createPortalSession();
                                                    window.location.href = sessionUrl;
                                                } catch (err) {
                                                    logger.error('Failed to open billing portal:', err);
                                                    alert(t('dashboard.failed_billing'));
                                                }
                                            }}
                                        >
                                            <CreditCardIcon /> {t('dashboard.manage_billing')}
                                        </button>
                                    ) : (
                                        <div className="account-actions-stack">
                                            <button className="upgrade-action-btn" onClick={() => setShowUnlockModal(true)}>
                                                <PremiumIcon size={18} /> {t('dashboard.upgrade_premium')}
                                            </button>
                                            {trips.some(t => t.hasTripAccess) && (
                                                <button
                                                    className="view-invoices-btn"
                                                    onClick={async () => {
                                                        try {
                                                            const { sessionUrl } = await createPortalSession();
                                                            window.location.href = sessionUrl;
                                                        } catch (err) {
                                                            logger.error('Failed to open billing portal:', err);
                                                            alert(t('dashboard.failed_billing'));
                                                        }
                                                    }}
                                                >
                                                    <InvoiceIcon /> {t('dashboard.view_invoices')}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Purchased Trips Section */}
                                {trips.filter(t => t.hasTripAccess).length > 0 && (
                                    <div className="purchased-trips-section">
                                        <h4>{t('dashboard.purchased_trips')}</h4>
                                        <div className="purchased-trips-list">
                                            {trips.filter(t => t.hasTripAccess).map(tripInfo => (
                                                <div
                                                    key={tripInfo.trip.id}
                                                    className="purchased-trip-item"
                                                    onClick={() => navigate({ to: '/trip/$slug' as any, params: { slug: tripInfo.trip.slug } as any })}
                                                >
                                                    <div className="trip-item-icon"><UnlockIcon /></div>
                                                    <div className="trip-item-info">
                                                        <span className="trip-item-title">{tripInfo.content.title || t('dashboard.untitled_trip')}</span>
                                                        <span className="trip-item-meta">{t('dashboard.full_access')}</span>
                                                    </div>
                                                    <ArrowRightIcon />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Option to buy more trips */}
                                {!status?.isPremium && (
                                    <div className="buy-more-section">
                                        <p className="buy-more-hint">{t('dashboard.buy_trip_hint')}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeSection === 'settings' && (
                            <div className="settings-section-view">
                                <div className="settings-group">
                                    <label>{t('dashboard.preferred_currency')}</label>
                                    <select defaultValue="EUR">
                                        <option value="EUR">€ EUR</option>
                                        <option value="USD">$ USD</option>
                                        <option value="RON">lei RON</option>
                                    </select>
                                </div>
                                <div className="settings-group">
                                    <label>{t('dashboard.interface_language')}</label>
                                    <select
                                        value={i18n.language.split('-')[0]}
                                        onChange={(e) => {
                                            const newLang = e.target.value;
                                            i18n.changeLanguage(newLang);
                                            // Handle redirection if needed
                                            const currentPath = window.location.pathname;
                                            if (currentPath.startsWith('/en/') || currentPath.startsWith('/ro/')) {
                                                const segments = currentPath.split('/');
                                                segments[1] = newLang;
                                                navigate({ to: segments.join('/') as any });
                                            }
                                        }}
                                    >
                                        <option value="en">English (US)</option>
                                        <option value="ro">Română</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {activeSection === 'privacy' && status && (
                            <PrivacySection userStatus={status} onSettingsChange={refreshStatus} />
                        )}
                    </div>
                </main>
            </div>

            <UnlockModal
                isOpen={showUnlockModal}
                onClose={() => setShowUnlockModal(false)}
                userStatus={status}
                trigger="banner"
            />
        </div >
    );
}

function TripCard({ tripInfo, onClick, style, t }: { tripInfo: TripInfo; onClick: () => void; style?: React.CSSProperties; t: any }) {
    const { trip, content } = tripInfo;
    const firstCity = content.cities?.[0];
    const dayCount = content.cities?.reduce((sum, c) => sum + c.days.length, 0) || 0;

    return (
        <div className="dashboard-trip-card" onClick={onClick} style={style}>
            <div className="card-thumb">
                <img
                    src="/images/photo-1469854523086-cc02fe5d8800.jpg"
                    alt={content.title || 'Trip'}
                    className="trip-card-image"
                />
                {trip.isPublic && <span className="card-badge">{t('dashboard.template')}</span>}
            </div>
            <div className="card-content">
                <h4>{content.title || t('dashboard.untitled_trip')}</h4>
                <p><PinIcon size={12} /> {firstCity?.name || t('dashboard.various')} • {dayCount} {t('dashboard.days')}</p>
            </div>
        </div>
    );
}

// Icons
const TripsIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
    </svg>
);
const CalendarIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
);
const PrivacyIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
);
const AccountIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
    </svg>
);
const SettingsIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"></circle>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
    </svg>
);
const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
);
const PlusIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);
const PremiumIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
);
const LogoutIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
        <polyline points="16 17 21 12 16 7"></polyline>
        <line x1="21" y1="12" x2="9" y2="12"></line>
    </svg>
);
const StarIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
    </svg>
);
const PinIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
        <circle cx="12" cy="10" r="3"></circle>
    </svg>
);
const CreditCardIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
        <line x1="1" y1="10" x2="23" y2="10"></line>
    </svg>
);
const UnlockIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
    </svg>
);
const ArrowRightIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
);
const InvoiceIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <path d="M14 2v6h6"/>
        <path d="M16 13H8"/>
        <path d="M16 17H8"/>
        <path d="M10 9H8"/>
    </svg>
);
